import z, {
  object,
  string,
  nativeEnum,
  boolean,
  literal,
  discriminatedUnion,
  preprocess,
} from 'zod';

import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { IS_POSITIVE, IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { Award_Per_Counting_Strategy } from '@occc/graphql';
import { ProductGroupsSchema, withIsEnabled } from '@occc/validation';
import {
  LimitDisabledSchemaWithIsEnabled,
  LimitSchema,
  validateMembershipLessThanGlobal,
} from '@occc/feat-limits';

import { BaseConfigurationSchema } from './base-configuration.schema';

import { PRODUCT_DISCOUNT_TYPE, AWARD_TYPES } from '../types';

const AwardCountEnabledSchema = object({
  membership: LimitSchema,
  global: LimitSchema,
}).superRefine((data, ctx) => {
  validateMembershipLessThanGlobal({
    membership: data.membership,
    global: data.global,
    ctx,
  });
});

const AwardCountDisabledSchema = object({
  membership: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  global: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
});

const AwardCountSchema = withIsEnabled(AwardCountEnabledSchema, AwardCountDisabledSchema);

const ProductDiscountEarningMechanismBaseSchema = object({
  productGroups: ProductGroupsSchema,
  displayForOrderAhead: boolean().optional(),
});

const ProductDiscountEarningMechanismSchema = discriminatedUnion('type', [
  ProductDiscountEarningMechanismBaseSchema.extend({
    type: literal(PRODUCT_DISCOUNT_TYPE.FREE),
    amount: string().optional(),
    discountType: nativeEnum(Award_Per_Counting_Strategy).optional(),
  }),
  ProductDiscountEarningMechanismBaseSchema.extend({
    type: literal(PRODUCT_DISCOUNT_TYPE.DISCOUNTED),
    amount: string({ required_error: IS_REQUIRED })
      .trim()
      .min(1, IS_REQUIRED)
      .refine((value) => Number(value) > 0, {
        message: IS_POSITIVE,
      }),
    discountType: nativeEnum(Award_Per_Counting_Strategy, {
      required_error: IS_REQUIRED,
    }),
    allowCarryOver: boolean().optional().default(false),
  }),
  ProductDiscountEarningMechanismBaseSchema.extend({
    type: literal(PRODUCT_DISCOUNT_TYPE.OVERRIDE),
    amount: string({ required_error: IS_REQUIRED })
      .trim()
      .min(1, IS_REQUIRED)
      .refine((value) => Number(value) > 0, {
        message: IS_POSITIVE,
      }),
    discountType: nativeEnum(Award_Per_Counting_Strategy).optional(),
  }),
]);

export const ProductDiscountSchema = BaseConfigurationSchema.extend({
  awardType: literal(AWARD_TYPES.PRODUCT),
  productDiscount: preprocess((val) => val ?? {}, ProductDiscountEarningMechanismSchema),
  awardCount: AwardCountSchema,
});

export type ProductDiscountFormValues = z.infer<typeof ProductDiscountSchema>;

export const validateProductDiscountForm = makeValidate(ProductDiscountSchema);
